import { unwrapResult } from '@reduxjs/toolkit';
import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, FieldArray } from 'formik';
import { Box, Button } from '@rsos/base-components';
import {
  setSharedWithAgencies,
  shareEmergencyCall,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice.js';
import { setSelectedTab } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { type AgencyConnection } from '@rsos/reduxDataStore/src/features/peerAgencies/peerAgencies.types';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import { notifyError } from '@rsos/utils/toastUtils';
import { FieldNotes } from '../';
import { AppState } from '../../../../types/AppState';
import { toastShared } from '../../../AgencyShareChat/utils/agencyShareChatNotificationHelpers';
import type {
  FormValues,
  ShareEmergencyCallResponse,
  ShareEmergencyCallError,
  ShareEmergencyCallRequest,
} from '../../types';
import ShareEventECCs from './ShareEventECCs';

const API_ERROR_MSG = 'We encountered an error while sharing this call.';

const EccShare = ({ onClose }: { onClose: () => void }) => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [agencyDisplayNameList, setAgencyDisplayNameList] = useState<string[]>(
    [],
  );

  const dispatch = useDispatch();

  const orgName = useCurrentOrgName();
  const { currentPSAP } = useSelector((state: AppState) => state.psaps);
  const isLoading = useSelector(
    (state: AppState) =>
      state.emergencyCalls.loading?.shareEmergencyCall || false,
  );
  const selectedCallerID = useSelector(
    (state: AppState) => state.emergencyCalls.selectedCallerID,
  ) as string;
  const sharedWithAgencies = useSelector(
    (state: AppState) =>
      (selectedCallerID &&
        state.emergencyCalls.callQueue[selectedCallerID]?.agencyShareChat
          ?.sharedWithAgencies) ||
      [],
  );
  const currentCall = useSelector((state: AppState) =>
    selectedCallerID
      ? state.emergencyCalls.callQueue[selectedCallerID]
      : undefined,
  );

  const agencyLinksTo = useSelector(
    (state: AppState) => state.peerAgencies?.agencyLinks?.linksTo,
  );

  const peerAgencies: AgencyConnection[] = useMemo(() => {
    return agencyLinksTo && agencyLinksTo.length
      ? agencyLinksTo.filter(
          (agency: AgencyConnection) => agency.status === 'active',
        )
      : [];
  }, [agencyLinksTo]);

  const { mostRecentLocation } = currentCall?.core?.locations
    ? getMostRecentLocations(currentCall)
    : { mostRecentLocation: null };

  const initialValues: FormValues = useMemo(
    () => ({
      selectedAgencies: sharedWithAgencies,
      notes: '',
    }),
    [sharedWithAgencies],
  );

  /**
   * get list of event chat participants for the ChatHeader component
   */
  const getEventChatParticipants = (agencyAccountIDs: string[]) => {
    const currentAgency = {
      account_id: currentPSAP.account_id,
      display_name: currentPSAP.name,
      contact_phone: currentPSAP?.contact_phone || '',
    };
    const sharedAgencies = [];
    sharedAgencies.push(currentAgency);

    agencyAccountIDs.forEach(accountID => {
      peerAgencies.forEach(agency => {
        if (agency.link_to.account_id === accountID) {
          const agencyData = {
            account_id: agency.link_to.account_id,
            display_name: agency.link_to.display_name,
          };
          sharedAgencies.push(agencyData);
        }
      });
    });

    return sharedAgencies;
  };

  const handleSubmit = (values: FormValues) => {
    const agencyAccountIDs = values.selectedAgencies;
    const message = values.notes;

    const shareableEvent = {
      ...mostRecentLocation,
      agencyShareChat: {
        ...mostRecentLocation?.agencyShareChat,
        eventChatParticipants: getEventChatParticipants(agencyAccountIDs),
        eventSharedBy: orgName,
      },
    };

    const filteredIDs: string[] = [];
    agencyAccountIDs.forEach(id => {
      if (!sharedWithAgencies.includes(id)) filteredIDs.push(id);
    });

    const reduxData = {
      accountIDs: agencyAccountIDs,
      callerID: selectedCallerID,
      chatParticipants: shareableEvent?.agencyShareChat?.eventChatParticipants,
      conversationID: null as string | null,
    };

    const requestData: ShareEmergencyCallRequest = {
      event: shareableEvent,
      eventType: shareableEvent?.agencyShareChat?.eventType,
      message: message || '',
      uri: `tel://${selectedCallerID}`,
      orgName: orgName,
      shareWith: filteredIDs,
    };

    const currentCallADR = JSON.parse(
      JSON.stringify(currentCall?.additionalData || {}),
    );

    if (Object.keys(currentCallADR).length) {
      requestData.adrData = currentCallADR;
    }

    dispatch(shareEmergencyCall(requestData))
      .then(unwrapResult)
      .then((response: ShareEmergencyCallResponse) => {
        reduxData.conversationID = response.conversation_id;
      })
      .then(() => {
        dispatch(setSharedWithAgencies(reduxData));

        agencyDisplayNameList.forEach(agencyName => {
          let source = 'unknown';
          if ('source' in shareableEvent) {
            source = shareableEvent.source;
          }
          toastShared(agencyName, source);
        });

        dispatch(setSelectedTab('chat'));
        onClose();
      })
      .catch((error: ShareEmergencyCallError) => {
        const detail = error?.data?.detail;
        if (detail && typeof detail === 'object') {
          const detailValue = detail as Record<string, string[]>;
          Object.keys(detailValue).forEach(key => {
            notifyError(`${key}: ${detailValue[key][0]}`);
          });
        } else if (error?.data?.detail) {
          const errorMessage = error.data.detail
            ? error.data.detail
            : API_ERROR_MSG;
          notifyError(errorMessage as string);
        }
      });
  };

  const handleNext = () => {
    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={() => undefined}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <Box mb="24px">
            {currentStep === 1 && (
              <FieldArray name="selectedAgencies">
                {arrayHelpers => (
                  <ShareEventECCs
                    selectedAgencies={values.selectedAgencies}
                    peerAgencies={peerAgencies}
                    arrayHelpers={arrayHelpers}
                    setFieldValue={setFieldValue}
                    onAgencyDisplayNameChange={setAgencyDisplayNameList}
                  />
                )}
              </FieldArray>
            )}

            {currentStep === 2 && (
              <FieldNotes
                value={values.notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFieldValue('notes', e.target.value)
                }
              />
            )}
          </Box>

          <Box horizontal flow={8}>
            {currentStep === 1 ? (
              <>
                <Button btnType="tertiary" type="button" grow onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  btnType="primary"
                  type="button"
                  onClick={handleNext}
                  disabled={values.selectedAgencies.length === 0}
                  grow
                >
                  Next
                </Button>
              </>
            ) : (
              <>
                <Button
                  btnType="tertiary"
                  type="button"
                  onClick={handleBack}
                  grow
                >
                  Back
                </Button>
                <Button
                  btnType="primary"
                  type="submit"
                  onClick={() => handleSubmit(values)}
                  grow
                  isLoadingProp={isLoading}
                  disabled={
                    !values.selectedAgencies?.length ||
                    values.selectedAgencies === sharedWithAgencies
                  }
                >
                  Share
                </Button>
              </>
            )}
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default EccShare;
